import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../Redux/Hooks";
import {
  getWalletBalance,
  getWhatsAppConversations,
  getWhatsAppMessages,
  sendWhatsAppMessage,
} from "../Redux/ApiCalls/Dashboard/WhatsAppAPI";
import { setActiveConversation } from "../Redux/Slice/Dashboard/WhatsAppSlice";
import type { WhatsAppConversationItem, WhatsAppSendMessageRequestPayload } from "../Data/whatsapp.dto";

const PAGE_SIZE = 100;

/**
 * Hook for WhatsApp conversations: paginated list, messages, send message.
 * Matches the React Native app's ConversationScreen + ChatScreen flow.
 */
export function useConversations() {
  const dispatch = useAppDispatch();
  const {
    conversations,
    conversationsPage,
    conversationsHasMore,
    conversationsLoading,
    activeConversation,
    messages,
    messagesPage,
    messagesHasMore,
    messagesLoading,
    sendingMessage,
    error,
    isError,
  } = useAppSelector((state) => state.whatsapp);

  // Fetch conversations (page 1 on mount)
  const fetchConversations = useCallback(
    (page: number = 1) => {
      dispatch(
        getWhatsAppConversations({
          page,
          page_size: PAGE_SIZE,
        })
      );
    },
    [dispatch]
  );

  const refresh = useCallback(() => {
    fetchConversations(1);
  }, [fetchConversations]);

  const loadMoreConversations = useCallback(() => {
    if (conversationsLoading || !conversationsHasMore) return;
    fetchConversations(conversationsPage + 1);
  }, [conversationsLoading, conversationsHasMore, conversationsPage, fetchConversations]);

  // Select a conversation and fetch its messages
  const selectConversation = useCallback(
    (conversation: WhatsAppConversationItem | null) => {
      dispatch(setActiveConversation(conversation));
      if (conversation) {
        dispatch(
          getWhatsAppMessages({
            customer_number: conversation.customer_number,
            page: 1,
            page_size: PAGE_SIZE,
          })
        );
      }
    },
    [dispatch]
  );

  // Load more messages for the active conversation
  const loadMoreMessages = useCallback(() => {
    if (!activeConversation || messagesLoading || !messagesHasMore) return;
    dispatch(
      getWhatsAppMessages({
        customer_number: activeConversation.customer_number,
        page: messagesPage + 1,
        page_size: PAGE_SIZE,
      })
    );
  }, [dispatch, activeConversation, messagesLoading, messagesHasMore, messagesPage]);

  const sendMessage = useCallback(
    (text: string, options?: Partial<WhatsAppSendMessageRequestPayload>) => {
      if (!activeConversation?.customer_number) {
        return Promise.reject(new Error("No conversation selected"));
      }
      return dispatch(
        sendWhatsAppMessage({
          to: activeConversation.customer_number,
          text,
          ...options,
        })
      )
        .unwrap()
        .then((response) => {
          dispatch(getWalletBalance());
          return response;
        });
    },
    [dispatch, activeConversation?.customer_number]
  );

  useEffect(() => {
    fetchConversations(1);
  }, [fetchConversations]);

  return {
    conversations,
    conversationsLoading,
    conversationsHasMore,
    activeConversation,
    messages,
    messagesLoading,
    messagesHasMore,
    sendingMessage,
    error,
    isError,
    refresh,
    loadMoreConversations,
    selectConversation,
    loadMoreMessages,
    sendMessage,
  };
}
